<template>
  <main id="mainContent" class="payment">
    <template>
      <PaymentAlert :hasError="ftHasPaymentError" :errorMessage="ftErrorMessage"/>
      <div class="app-main-content">
        
        <div class="app-content-panels"><!-- prevent form stretching to full width -->
          <!-- form -->
          <form class="app-panel-left">
            <p class="app-required-indicator __before" :class="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0 ? '' : 'noPayment'">Required field</p>
            <!-- CC #  -->
            <div class="app-input-row" v-if="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
              <ValidationObserver ref="ccNumberObserver">
                <ValidationProvider name="Credit card number" v-slot="{ errors }" :rules="ccNumberRules">
                  <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                    <label for="ccNumber" class="app-required-indicator __after">Credit card (Numbers only)</label>
                    <div class="cc-img">
                      <input type="text" id="ccNumber" v-model="myCardNumber" aria-describedby="ccNumberDesc"/>
                      <span class="accepted-cards">
                        <img src="@ridmv/web-themes/images/icons/img_visa.svg" alt="VISA">
                        <img src="@ridmv/web-themes/images/icons/img_master.svg" alt="MasterCard">
                        <img src="@ridmv/web-themes/images/icons/img_discover.svg" alt="Discover">
                        <img src="@ridmv/web-themes/images/icons/img_amex.svg" alt="American Express">
                      </span>
                    </div>
                    <p class="error-message">{{ errors[0] }}</p>
                    <p id="ccNumberDesc" class="sr-only">
                      Required. Accepted cards: VISA, MasterCard, Discover or American Express.
                    </p>
                  </div>
                </ValidationProvider>
              </ValidationObserver>
            </div>

            <div class="app-input-row" v-if="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
              <!-- exp date -->
              <div class="expiration-container">
                <div class="app-label-non-control app-required-indicator __after">Expiration date (MM/YYYY)</div>
                <div class="combo-inputs">
                  <ValidationObserver ref="expMonthObserver">
                    <ValidationProvider name="Month" v-slot="{ errors }" :rules="monthRules">
                      <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                        <label for="expireMonth" class="app-required-indicator __after sr-only">Expiration month</label>
                        <input type="text" id="expireMonth" class="exp-month" ref="expireMonth"
                          v-model="myExpireMonth" aria-describedby="expireDateDesc" @keyup="switchFocus"/>
                        <p hidden>{{ expMonthError = errors[0] || '' }}</p>
                      </div>
                    </ValidationProvider>
                  </ValidationObserver>

                  <span>/</span>

                  <ValidationObserver ref="expYearObserver">
                    <ValidationProvider name="Year" v-slot="{ errors }" :rules="yearRules">
                      <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                        <label for="expireYear" class="app-required-indicator __after sr-only">Expiration year</label>
                        <input type="text" id="expireYear" class="exp-year" ref="expireYear"
                          v-model="myExpireYear" aria-describedby="expireDateDesc"/>
                        <p hidden>{{ expYearError = errors[0] || '' }}</p>
                      </div>
                    </ValidationProvider>
                  </ValidationObserver>
                </div><!-- combo-inputs -->

                <!-- one-off due to mm-yyyy combo and limitation on validation lib's dependencies -->
                <p class="error-message">{{ expDateErrors }}</p>
                <p id="expireDateDesc" class="sr-only">Required. Two-digit month and four-digit year format.</p>
              </div><!-- /expiration-container -->

              <!-- cvv -->
              <ValidationObserver ref="cvvObserver">
                <ValidationProvider name="CVV" v-slot="{ errors }" :rules="cvvRules">
                  <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                    <label for="cvv" class="app-required-indicator __after">
                      CVV<span class="app-tooltip" tabindex="0"
                                v-tooltip="{ content: cvvDescription, offset: '5' }">
                        <img src="@ridmv/web-themes/images/icons/question-circle-solid.svg" alt="Description">
                        <span class="sr-only">{{ cvvDescription }}</span>
                    </span>
                    </label>
                    <input type="text" id="cvv" class="cvv" v-model="myCvv" aria-describedby="cvvDesc"/>
                    <p>{{ errors[0] }}</p>
                    <p id="cvvDesc" class="sr-only">Required. Three-digit format.</p>
                  </div>
                </ValidationProvider>
              </ValidationObserver>
            </div>

            <div class="app-input-row" v-if="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
              <!-- first name on card -->
              <ValidationObserver ref="firstNameObserver">
                <ValidationProvider name="First name" v-slot="{ errors }" rules="fieldRequired|specialChars|max:40">
                  <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                    <label for="firstName" class="app-required-indicator __after">First name</label>
                    <input type="text" id="firstName" v-model="myFirstName" aria-describedby="firstNameDesc"/>
                    <p>{{ errors[0] }}</p>
                    <p id="firstNameDesc" class="sr-only">Required.</p>
                  </div>
                </ValidationProvider>
              </ValidationObserver>

              <!-- last name on card -->
              <ValidationObserver ref="lastNameObserver">
                <ValidationProvider name="Last name" v-slot="{ errors }" rules="fieldRequired|specialChars|max:40">
                  <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                    <label for="lastName" class="app-required-indicator __after">Last name</label>
                    <input type="text" id="lastName" v-model="myLastName" aria-describedby="lastNameDesc"/>
                    <p>{{ errors[0] }}</p>
                    <p id="lastNameDesc" class="sr-only">Required.</p>
                  </div>
                </ValidationProvider>
              </ValidationObserver>
            </div>

            <div v-if="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
            <!-- company name on card -->
              <ValidationObserver ref="orgNameObserver">
                <ValidationProvider name="Organization name" v-slot="{ errors }" rules="specialChars|max:80">
                  <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                    <label for="companyName" style="padding-top: 0.2em">Company name</label>
                    <input type="text" id="companyName" class="co" v-model="myCompanyName" aria-describedby="companyNameDesc"/>
                    <p style="width: unset;">{{ errors[0] }}</p>
                    <p id="companyNameDesc" class="sr-only">Optional.</p>
                  </div>
                </ValidationProvider>
              </ValidationObserver>
            </div>

            <div><ContactInfo :verify="verify" @doContactValidated="setIndividualContact" ref="contact" /></div>
            
            <div class="fees" v-if="$store.state.guestLogin.totalBill  && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
                <div class="fees-list">
                  <p>Total Transaction Fee </p> <p v-if="$store.state.guestLogin.totalBill.rimsTotalFeeAmount">{{ toCurrencyNum($store.state.guestLogin.totalBill.rimsTotalFeeAmount) }}</p>
                </div>
                <div class="fees-list">
                  <p>Service Fee for the third party provider</p> <p>{{ toCurrencyNum($store.state.guestLogin.totalBill.forteServiceFee) }}</p> 
                </div>
                <div class="fees-list">
                  <p> <strong>Total Bill </strong></p> <p class="total-charge">{{ toCurrencyNum($store.state.guestLogin.totalBill.totalSumFeeAmount) }}</p>
                </div>
            </div>
              
            <BaseButtonGroup>
              <button type="button" @click="handleRouting(true)" :disabled="!btnComplete" :class="!btnComplete ? 'btnDisable': ''">Previous</button>
              <button type="submit" class="app-btn-primary" :disabled="!btnComplete"
                      @click.prevent="handleRouting(false)">Complete transaction(s)
              </button>
            </BaseButtonGroup>
          </form>
        </div>
      </div>
      <div v-if="$store.state.guestLogin.totalBill && $store.state.guestLogin.totalBill.totalSumFeeAmount > 0">
      <OneTimeToken :card-number="myCardNumber"
                    :card-month="myExpireMonth"
                    :card-year="myExpireYear"
                    :card-cvv="myCvv"
                    :fetch-token="fetchToken"
                    @doPay="parseForteResponse"/>
      </div>
    </template>
          <!-- full screen spinner -->
          <ModalSpinnerPaymentCompletion />
  </main>
</template>


<script>
  import _ from 'underscore'
  import appConfig from '@/app.config'
  import BaseButtonGroup from '@/components/ui/BaseButtonGroup'
  import {  mapActions } from 'vuex';
  import { setInteractionMode, ValidationProvider, ValidationObserver } from 'vee-validate'
  import ContactInfo from './ContactInfo.vue';
  import { toCurrency } from '@/utils/helpers';
  import OneTimeToken from '../../components/payment/OneTimeToken.vue';
  import PaymentAlert from '../../components/payment/PaymentAlert.vue';
  import getForteConfig from '../../components/payment/forteConfig';
  import { stripExtraSpaces } from '@/utils/utils';
  import { loadScript } from 'vue-plugin-load-script'
  import * as COMMONS from '../../store/_commons';
  import { zonedTimeToUtc } from 'date-fns-tz';
  import { phoneMixin } from '@/utils/phoneMixin'
  import ModalSpinnerPaymentCompletion from '@/components/appModal/ModalSpinnerPaymentCompletion';


  export default {
    name: 'Payment',
    page: {
      title: `Payment | ${appConfig.shortName}`,
      meta: [
        {
          name: 'description',
          content: 'Payment for the selected service.'
        }
      ]
    },
    components: {
      BaseButtonGroup,
      ValidationObserver,
      ValidationProvider,
      ContactInfo,
      OneTimeToken,
      PaymentAlert,
      ModalSpinnerPaymentCompletion
    },
    mixins: [
      phoneMixin,
    ],
    data() {
      return {
        stepNum: 5,
        screenData: {},
        enableNext: true,
        showError: false,
        year: this.$route.query.year,
        make: this.$route.query.make,
        model: this.$route.query.model,
        plateNumber: this.$route.query.plateNumber,
        plateType: this.$route.query.plateType,
        btnComplete: true,


        ftToken: undefined,
        ftHasPaymentError: false,
        ftErrorMessage: '',
        fetchToken: false,
  
        cvvDescription: 'For Discover, MasterCard and Visa, enter last three digits on the signature strip. ' +
            'For American Express, enter the four digits in small print on the front of the card.',
        myCardNumber: '',
        myExpireYear: '',
        myExpireMonth: '',
        myExpireDate: '',
        myCvv: '',
        myFirstName: '',
        myLastName: '',
        myCompanyName: '',
  
        // myBill: {},               // from api call
        myPaymentDate: undefined, // capture this for edge case, e.g. user makes res close to midnight
  
        expMonthError: '',
        expYearError: '',
        isAcceptedCardType: false,
  
        supportedCardTypes: [
          'amex',                 // both individual + corporate
          'disc',
          'mast',
          'visa'
        ],
  
        isModify: true,          // fail-safe only - Payment form should not show during res update
        verify: true,
        myContact: null,
        isConsentProvided: true,
       
        
      }
    },

    computed: {
      // ...mapState([
      //   'LANG',
      //   'STEPS',
      //   // 'PAGES'
      // ]),
 

      language() {
        return this.LANG;
      },

      ccNumberRules() {
        return {
          fieldRequired: true,
          numeric: true,
          minMax: [ 13, 19 ],
        };
      },
      cvvRules() {
        return {
          fieldRequired: true,
          numeric: true,
          minMax: [ 3, 4 ],
        };
      },
      monthRules() {
        return {
          fieldRequired: true,
          numeric: true,
          max: 2,
          monthBetween: [ 1, 12 ],
        }
      },
      yearRules() {
        return {
          fieldRequired: true,
          numeric: true,
          max: 4,
          validYear: [ 20 ],
        }
      },

      expDateErrors() {
        return `${ this.expMonthError } ${ this.expYearError }`;
      }
    },

    created() {
      document.body.setAttribute("tabindex", "1");
      document.body.focus();

      document.body.removeAttribute("tabindex");
      this.loadForteJs();

      setInteractionMode('lazy'); 
    },

    /**
     * IMPORTANT: Originally set to unload Forte script before component is destroyed. BUT if user happens to re-use
     * the same session, e.g. going from Payment screen back a few steps to pick a different time slot, the Forte object
     * initially created will still be hanging around. That is, unloading the Forte script does NOT destroy the Forte
     * object already instantiated. So, reloading the Forte script will create a _different_ Forte instance which
     * eventually leads to multiple payment calls (and reservation creations) based on the number of Forte objects
     * in memory. These Forte instances are not accessible directly.
     *
     * Cricket from Forte support on nullifying the Forte object(s)... Did not find any way within Forte script to
     * destroy an existing instance so not calling the unloading script here as a less-than-optimal solution.
     */
    beforeDestroy() {
      this.resetData();
    },

    methods: {
      ...mapActions({
        getPaymentProcess: COMMONS.GUEST_PAYMENTPROCESS,
        getFinalizecart: COMMONS.GUEST_FINALIZECART,
        getSendTransactionConfiramtion: COMMONS.GUEST_SENDTRANSACTIONCONFIRMATION,
        getValidatecart: COMMONS.GUEST_VALIDATECART,
      }),

      toCurrencyNum(value) {
        if (value != null && value != ''){
        return toCurrency(value);
        } else {
          return '$0.00'
        }
      },

      async handleRouting(isPrev) {
        if (isPrev) {
          this.$emit('getStep' , 4);
          this.$store.state.guestLogin.step = 4;
          this.$store.state.networkResponse = null;
        } else {
          // do nothing if [Next] is not enabled (use aria-disabled for better accessibility)
          this.$refs.contact.continueToNextStep();
          setTimeout(() => {
            this.isFormValid().then((result) => {
              if(result) {
                this.$emit('doResetVerify');
                this.btnComplete = false;
                // signal child component to get OTT IFF all form fields validated
                this.fetchToken = true;
                if(!this.$store.state.guestLogin.totalBill) {
                
                  this.withoutPayment();
                }
              }
            })
            // if (this.isFormValid()) {
              
            // }
          }, 5)
    
        }
      },
      

      /**
       * Starts Forte charging process.
       * @returns {Promise<void>}
       */
      async makePayment() {
           
        // let cartTxn = [];
        // for(var i=0; this.$store.state.guestLogin.renewalCart.length > i; i++) {
        //     cartTxn.push(this.$store.state.guestLogin.renewalCart[i].cartTransactions)
        // }
        for(let i in this.$store.state.guestLogin.cartTransactions.cartTransactions) {
                  this.$store.state.guestLogin.cartTransactions.cartTransactions[i].transactionFees = this.$store.state.guestLogin.renewalCart[i].onlineTransactionFeeCollection
              }
       const args = {
                cartPayment: {
                  authorizationNumber:  '',
                  paymentDate: this.getUtcDate(),
                  paymentMakerCompanyName: stripExtraSpaces(this.myCompanyName),
                  paymentMakerFirstName: stripExtraSpaces(this.myFirstName),
                  paymentMakerLastName: stripExtraSpaces(this.myLastName),
                  serviceFeeAmount: this.$store.state.guestLogin.totalBill.forteServiceFee,
                  totalCartAmount: this.$store.state.guestLogin.totalBill.totalSumFeeAmount,
                  traceNumber: '',
                  transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId,
                },
                cartTransactions : this.$store.state.guestLogin.cartTransactions.cartTransactions,
                email: this.$refs.contact.myEmail || '',
                isPaperlessCommunication: true,
                phone: this.formatPhoneForRims(this.$refs.contact.myPhone )|| '',
                transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId, 
              }
              const validateResults=await this.getValidatecart(args);     
              const validateCartRes = validateResults.data;


  if(validateResults.status==200 && validateCartRes.isCartValid==true)
      {
        if(this.$store.state.guestLogin.totalBill && this.$store.state.guestLogin.totalBill.totalSumFeeAmount > 0) {
          const ACTION = 'sale';  // always sale?
          try {
              for(let i in this.$store.state.guestLogin.cartTransactions.cartTransactions) {
                  this.$store.state.guestLogin.cartTransactions.cartTransactions[i].transactionFees = this.$store.state.guestLogin.renewalCart[i].onlineTransactionFeeCollection
              }
              const argsFinalize = {
                paymentRequest: {
                          action: ACTION,
                          subtotalAmount: this.$store.state.guestLogin.totalBill.rimsTotalFeeAmount,
                          serviceFeeAmount: this.$store.state.guestLogin.totalBill.forteServiceFee,
                          authorizationAmount: this.$store.state.guestLogin.totalBill.totalSumFeeAmount,
                          billingAddress: {
                            firstName: stripExtraSpaces(this.myFirstName),
                            lastName: stripExtraSpaces(this.myLastName)
                          },
                  card: {
                    oneTimeToken: this.ftToken
            }
                },
                transactionCart:{ 
                cartPayment: {
                  authorizationNumber: '',
                  paymentDate: this.getUtcDate(),
                  paymentMakerCompanyName: stripExtraSpaces(this.myCompanyName),
                  paymentMakerFirstName: stripExtraSpaces(this.myFirstName),
                  paymentMakerLastName: stripExtraSpaces(this.myLastName),
                  serviceFeeAmount: this.$store.state.guestLogin.totalBill.forteServiceFee,
                  totalCartAmount: this.$store.state.guestLogin.totalBill.totalSumFeeAmount,
                  traceNumber: '',
                  transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId,
                },
                cartTransactions : this.$store.state.guestLogin.cartTransactions.cartTransactions,
                email: this.$refs.contact.myEmail || '',
                isPaperlessCommunication: true,
                phone: this.formatPhoneForRims(this.$refs.contact.myPhone) || '',
                transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId, 
              },
              isConsentProvided: true,
            };

             const finalizeCart = await this.getFinalizecart(argsFinalize);
            

             if (finalizeCart.status == 200) {
              const finalizeRes=finalizeCart.data;
              this.$store.state.guestLogin.step = 6;
              this.$store.state.guestLogin.resNotificationSent= finalizeRes.sendConfirmationResponse;
          
             }
              else
              {
              console.error(`Finalize Cart failed...`);
              }
          } catch (err) {
           console.error(`Finalize Cart failed...`, err.message);
            this.btnComplete = true;
          }
        } else {
          this.withoutPayment();
        }
      } else {
              
              this.setPaymentAlert(validateCartRes.message);
        }
      },  
      

      async withoutPayment() {
        
        try{

          for(let i in this.$store.state.guestLogin.cartTransactions.cartTransactions) {
            this.$store.state.guestLogin.cartTransactions.cartTransactions[i].transactionFees = this.$store.state.guestLogin.renewalCart[i].onlineTransactionFeeCollection
          }
            const args = {
             
              cartTransactions : this.$store.state.guestLogin.cartTransactions.cartTransactions,
              email: this.$refs.contact.myEmail || '',
              isPaperlessCommunication: true,
              phone: this.formatPhoneForRims(this.$refs.contact.myPhone) || '',
              transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId, 
            }
           
              const validateResults=await this.getValidatecart(args);              
              const validateCartRes = validateResults.data;

      if(validateResults.status==200 && validateCartRes.isCartValid==true)
      {
        const argsFinalize = {      
            transactionCart:{        
              cartTransactions : this.$store.state.guestLogin.cartTransactions.cartTransactions,
              email: this.$refs.contact.myEmail || '',
              isPaperlessCommunication: true,
              phone: this.formatPhoneForRims(this.$refs.contact.myPhone) || '',
              transactionCartId: this.$store.state.guestLogin.cartTransactions.transactionCartId, 
            },
            isConsentProvided: true,
           }
            const finalizeCart = await this.getFinalizecart(argsFinalize);
            
            if (finalizeCart.status == 200) {
              const finalizeRes=finalizeCart.data;
              this.$store.state.guestLogin.step = 6;
              this.$store.state.guestLogin.resNotificationSent= finalizeRes.sendConfirmationResponse;
             }
              else
              {
              console.error(`Finalize Cart failed...`);
              this.btnComplete = true;
              }
        }
        else {
              
              this.setPaymentAlert(validateCartRes.message);
        }
        } catch (err) {
          console.error(`Forte payment failed...`, err.message);
          this.btnComplete = true;
        }
      },

      /**
       * Converts local/client datetime to UTC and send it back to RIMS.
       * Note: RIMS persists all date(time) in database in ET timezone. Attempted to pre-convert UTC to EST
       * for RIMS... But it appears Chrome (v98) got confused in the process. Decided to send UTC along as
       * it's the standard. RIMS will have to deal with the datetime conversion on the backend.
       * @returns {Date}
       */
      getUtcDate(dt) {
        // [1] get client local datetime AND timezone
        const clientDate = dt || new Date();
        const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // [2] convert client local datetime to UTC (must specify local timezone)
        // [3] send UTC datetime to RIMS (browser will get confused if UTC is converted to ET before sending... 😖)
        return zonedTimeToUtc(clientDate, clientTimezone);
      },

      setIndividualContact(data) {
        this.myContact = data;
        this.isFormValid();
      },


      /**
       * Checks card type returned by Forte to ensure it's one of the business-predefined supported cards.
       * Note:
       *    Forte will always return 'success' and OTT even if card type is NOT supported.
       *    Forte OTT will return an error if:
       *      (1) The card type does not match the card number
       *      (2) The card is expired
       *      (3) The card number format is incorrect (including cvv)
       *      (4) Any required field is missing
       * @param type The card_type value from Forte OTT response.
       */
      isCardTypeSupported(type) {
        return _.find(this.supportedCardTypes, item => item === type);
      },

      async isFormValid() {
        if(this.$store.state.guestLogin.totalBill && this.$store.state.guestLogin.totalBill.totalSumFeeAmount > 0) {
          const ccNumberValid = await this.$refs.ccNumberObserver.validate();
          const firstNameValid = await this.$refs.firstNameObserver.validate();
          const lastNameValid = await this.$refs.lastNameObserver.validate();
          const orgNameValid = await this.$refs.orgNameObserver.validate();
          const monthValid = await this.$refs.expMonthObserver.validate();
          const yearValid = await this.$refs.expYearObserver.validate();
          const cvvValid = await this.$refs.cvvObserver.validate();
          const myContactValid =  await this.$refs.contact.hasContactInfo();

          this.$emit('doResetVerify');
          let phonevalidate = await this.$refs.contact.isPhoneValidated;
          let emailvalidate = await this.$refs.contact.isEmailValidated;
          // let myNotificationValid = await this.$refs.contact.myNotification;
          this.$refs.contact.toggleRequirementError();

          return (ccNumberValid && firstNameValid && lastNameValid && orgNameValid && monthValid && yearValid && cvvValid && myContactValid && !phonevalidate && emailvalidate);
        }
        else {
          const myContactValid =  await this.$refs.contact.hasContactInfo();

          this.$emit('doResetVerify');
          let phonevalidate = await this.$refs.contact.isPhoneValidated;
          let emailvalidate = await this.$refs.contact.isEmailValidated;
          // let myNotificationValid = await this.$refs.contact.myNotification;
          this.$refs.contact.toggleRequirementError();

          return( myContactValid && !phonevalidate && emailvalidate);
        }
      },


      /**
       * Ensures Forte response _really really_ is good to go before processing payment.
       * @param data
       */
      parseForteResponse(data) {
        const res = data.forte;

        if (data.hasOtt) {
          // forte OTT fetch succeeds... but is the OTT _really_ good for payment?
          this.verifyOtt(res);
        } else {
          // forte OTT fetch fails
          this.setPaymentAlert(this.parseOttError(res.response_description));
          this.btnComplete = true;
        }

        // always reset for the next round
        this.fetchToken = false;
      },


      /**
       * Converts Forte OTT response description to proper English.
       * @param desc
       * @returns {string|*}
       */
      parseOttError(desc) {
        if (desc.endsWith('invalid.')) {
          let msg = desc.replace('cvv', 'CVV');
          msg = msg.replace('expire_month and expire_year', 'expiration month and/or expiration year');
          msg = msg.split('_').join(' ');   // replaceAll won't work on older iOS
          return msg;
        }
        return '';
      },

      /**
       * Confirms that the OTT returned by Forte contains a supported card type before making payment.
       * @param res
       */
      verifyOtt(res) { 
        const supported = this.isCardTypeSupported(res.card_type);
        // make payment IFF OTT returns a supported card type
        if (supported === undefined) {
          this.setPaymentAlert('Sorry, we only support VISA, MasterCard, Discover and American Express.');
          this.btnComplete = true;
        } else {
          // remove existing alert banner, if any
          this.ftHasPaymentError = false;
          this.ftErrorMessage = '';
          this.ftToken = res.onetime_token;
          this.makePayment();
        }
      },
      /**
       * Resets Forte data just in case. This won't force Forte to destroy any existing Forte instance.
       * See comment in beforeDestroy().
       */
      resetData() {
        this.ftToken = '';
        this.ftHasPaymentError = false;
        this.ftErrorMessage = '';
      },

      setPaymentAlert(msg) {
        this.ftToken = '';  // MUST reset OTT or payment will repeat based on # of errors prior to form submit
        this.ftHasPaymentError = true;
        this.ftErrorMessage = msg;
      },

      /**
       * Jumps from MM to YYYY.
       * @param e
       */
      switchFocus(e) {
        const isTab = e.key === 'Tab' || e.keyCode === 9;
        const isShift = e.key === 'Shift' || e.keyCode === 16;
  
        if (!isTab && !isShift) {
          const ref = e.target.id;
          if (ref === 'expireMonth' && this.myExpireMonth.length === 2) {
            this.$refs.expireYear.focus();
          }
        }
      },
      /**
       * Loads Forte JS library only when needed.
       * For some reason, the script MUST be loaded here instead of inside OneTimeToken component.
       */
      async loadForteJs() {
        const config = getForteConfig();
        console.log('forte js to be loaded...', config.url);

        loadScript(config.url)
          .then(() => {
            // don't call getFees here to avoid dependency
          })
          .catch(error => {
            // this should catch any network call error (no requirements specified)
            console.error(error);
          });
      },
    },

    mounted() {
      window.scroll(0,0);
      document.getElementById("mainContent").focus();
    }
    
  }
</script>
  
<style lang="scss" scoped>
  .payment {
    input {
      &.exp-month, &.exp-year, &.cvv {
        text-align: center;
      }
      &.co {
        width: 100%;
      }
      &.exp-month {
        max-width: 3.5em;
      }
      &.exp-year, &.cvv {
        max-width: 5em;
      }
    }

    .noPayment {
      margin-bottom: 1rem;
    }
  
    .cc-img {
      display: flex;
      flex-wrap: wrap;
      #ccNumber {
        margin-right: 0.75em;
        width: 330px;
      }
      .accepted-cards {
        display: flex;
        gap: 0.5em;
        img {
          margin-top: 0.15em; // for wrapped images on small screens
          width: 39px;
        }
      }
      ~ .error-message {
        color: var(--ri-red);
        @extend %font-sans-regular;
        padding-top: 0.5em;
      }
    }
  
    .expiration-container {
      // could limit container width, if needed
      .combo-inputs {
        display: flex;
        gap: 0.35em;
        span:nth-child(2) {
          padding-top: 0.65em;
        }
      }
    }
  
    .error-message {
      color: var(--ri-red);
      line-height: 1.35em;
      padding-top: unset;
      padding-bottom: 1.25em;
    }
  }
  
  .fees {
    background-color: #1B243B;
    color: white;
    margin-bottom: 10px;
    .fees-list{
        display: flex;
        justify-content: space-between;
        p {
          font-size: 16px;
          line-height: 23px;
          padding: 10px;
        }
        .total-charge{
          color: #FFA500;
          font-weight: 700;
          width: 100px;
          text-align: right;
        }
    }
  }

  .btnDisable{
    background-color: #fff !important;
    color: var(--ri-blue);
  }

  @media only screen and (min-width: 500px) and (max-width: 743px) {
    .co {
        width: 420px !important;
      }
  }
</style>
  